'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'

import { resolveFeatureAccess } from './use-feature-access'

let batchCache: { data: Record<string, boolean>; at: number } | null = null
const CACHE_TTL = 120_000

/**
 * Check access for multiple features in parallel. Returns a map of feature → boolean.
 * Caches the result for 2 minutes. Defaults to false (hidden) until loaded.
 */
export function useFeatureAccessBatch(features: string[]) {
  const { user } = useAuth()
  const [accessMap, setAccessMap] = useState<Record<string, boolean>>(batchCache?.data ?? {})
  const [isLoading, setIsLoading] = useState(true)
  const prevUserRef = useRef<string | null>(null)
  const featuresKey = [...features].sort().join(',')

  const fetchAll = useCallback(async () => {
    if (!user || features.length === 0) return

    if (batchCache && Date.now() - batchCache.at < CACHE_TTL) {
      const allCached = features.every((f) => f in batchCache!.data)
      if (allCached) {
        setAccessMap(batchCache.data)
        setIsLoading(false)
        return
      }
    }

    try {
      // Route through the shared resolver so flags requested by both this batch
      // hook and the singular `useFeatureAccess` hook (e.g. `cloud_agent_ide_v2`)
      // share one in-flight request + cache instead of double-fetching.
      const results = await Promise.all(
        features.map(async (feature) => {
          const state = await resolveFeatureAccess(feature, user.id)
          return [feature, state.has_access] as const
        })
      )
      const map = Object.fromEntries(results)
      batchCache = { data: { ...(batchCache?.data ?? {}), ...map }, at: Date.now() }
      setAccessMap(batchCache.data)
    } finally {
      setIsLoading(false)
    }
  }, [user, featuresKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const userId = user?.id ?? null
    if (!userId) return

    if (prevUserRef.current === userId && !isLoading) return

    prevUserRef.current = userId
    setIsLoading(true)
    fetchAll()
  }, [user, fetchAll]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasAccess = useCallback(
    (feature: string) => {
      if (isLoading) return false
      return accessMap[feature] ?? false
    },
    [accessMap, isLoading]
  )

  return { accessMap, hasAccess, isLoading }
}

/** Invalidate the batch cache (e.g. after admin changes). */
export function invalidateFeatureAccessCache() {
  batchCache = null
}
